/* eslint-disable @typescript-eslint/no-explicit-any */
import { setDataValue } from '@juvare/json-data-util';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { Button, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { buildAlertTab, buildEventTab, buildGeneralTab, buildHiddenTab, buildModalTab, buildStyleTab, showWidgetEditDialog, } from '../WidgetEditDialog';
import { callConfigFunction, defaultWidgetLabelProvider, getLabelAndValuePairsFromChildProps, handleWidgetOnLoad, handleWidgetUpdate, joinLabels, makeNewConfigUnique, renderForm, setModalChildSkipValidate, } from '../WidgetForm/Utils';
import { TemplateOutput } from '../WidgetFormContent/Components';
import { BaseLayoutColumn } from '../WidgetFormPanel/Components';
import { WidgetHandler } from '../WidgetHandler';
import { EditModeDialog, ModalDialogForm, Panel } from './Components';
import { type } from './types';
const WidgetFormModalPanel = (props) => {
    var _a, _b, _c, _d, _e;
    const childProps = (_a = props.childProps) !== null && _a !== void 0 ? _a : [];
    const { t } = useCommonFormUiTranslation();
    const getModalChildProps = () => {
        if (props.config.children) {
            return childProps.slice(props.config.children.length);
        }
        else {
            return childProps;
        }
    };
    const getPanelChildProps = () => {
        if (props.config.children) {
            return childProps.slice(0, props.config.children.length);
        }
        else {
            return [];
        }
    };
    const showModal = () => {
        const data = props.getData();
        props.internalData.modalState = { open: true, data: data != null ? JSON.parse(JSON.stringify(data)) : {} };
        props.update();
        renderForm(props);
    };
    const onSave = (data) => {
        props.internalData.modalState.open = false;
        const dataPath = props.dataPath
            .split('.')
            .filter((part) => part !== '' && !part.startsWith('!'))
            .join('.');
        if (dataPath === '') {
            if (typeof data === 'object' && !Array.isArray(data)) {
                for (const [key, value] of Object.entries(data)) {
                    setDataValue(props.rootData, key, value);
                }
                props.update();
            }
            else {
                console.error('Modal Panel must return an object when its data path is not set');
            }
        }
        else {
            props.setData(data);
        }
        renderForm(props, { changed: true, autoSaveImmediately: props.config.autoSave });
    };
    const onCancel = () => {
        props.internalData.modalState.open = false;
        props.update();
        renderForm(props);
    };
    const onCloseAfterTransition = () => {
        delete props.internalData.modalState;
        props.update();
        renderForm(props);
    };
    let alert = props.config.alert;
    if (alert === null || alert === void 0 ? void 0 : alert.custom) {
        const handler = new WidgetHandler(props);
        alert = callConfigFunction((_b = props.config.alert) === null || _b === void 0 ? void 0 : _b.custom, 'alert custom', 'value, handler', props.getData(), handler);
    }
    return (React.createElement(Panel, { variant: (_c = props.config.style) === null || _c === void 0 ? void 0 : _c.variant, label: props.config.label, alert: alert, actions: props.config.editButtonLabel ? (React.createElement(Button, { color: 'primary', sx: { alignSelf: { xs: 'flex-end', sm: 'center' } }, startIcon: React.createElement(DriveFileRenameOutlineIcon, null), onClick: showModal, tabIndex: props.editMode ? -1 : undefined, "data-test": 'edit-button', autoFocus: (_d = (!props.editMode && props.config.autoFocus)) !== null && _d !== void 0 ? _d : false }, props.config.editButtonLabel)) : (React.createElement(IconButton, { color: 'primary', sx: { alignSelf: { xs: 'flex-end', sm: 'center' } }, onClick: showModal, "aria-label": 'Show ' + props.config.label + ' Form', tabIndex: props.editMode ? -1 : undefined, "data-test": 'edit-button', autoFocus: (_e = (!props.editMode && props.config.autoFocus)) !== null && _e !== void 0 ? _e : false },
            React.createElement(DriveFileRenameOutlineIcon, null))) },
        props.editMode ? (React.createElement(EditModeDialog, { config: props.config },
            React.createElement(BaseLayoutColumn, { spacing: 16, childrenEditorPath: 'modal.children', childProps: getModalChildProps(), renderData: props.renderData, editMode: props.editMode, dragTargetPlaceholderMessage: t('widget.widgetForm.dropHere') }))) : (React.createElement(React.Fragment, null, props.internalData.modalState && (React.createElement(ModalDialogForm, { open: props.internalData.modalState.open, data: props.internalData.modalState.data, parentModalWidgetHandler: new WidgetHandler(props), formParams: props.rootParams.formParams, valueProviders: props.rootParams.valueProviders, subFormProviders: props.rootParams.subFormProviders, globalWidgetConfig: props.rootParams.globalWidgetConfig, rules: props.rootParams.rules, widgets: props.widgets, onSave: onSave, onCancel: onCancel, onCloseAfterTransition: onCloseAfterTransition, config: props.config })))),
        props.config.template && (React.createElement(Typography, { component: 'div', "data-test": 'template' },
            React.createElement(TemplateOutput, { template: props.config.template, props: props }))),
        props.config.render && props.config.render(new WidgetHandler(props)),
        props.config.children && (React.createElement(BaseLayoutColumn, { spacing: 16, childrenEditorPath: 'children', childProps: getPanelChildProps(), renderData: props.renderData, editMode: props.editMode }))));
};
const WidgetFormModalPanelEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('widget.widgetModal.modalPanel') + ': ' + props.config.label }, buildGeneralTab(props, {
            keyOptional: true,
            label: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.text',
                    key: 'editButtonLabel',
                    label: t('widget.widgetButton.editButtonLabel'),
                    emptyAsNull: true,
                },
                {
                    type: 'form.template',
                    key: 'template',
                    label: t('widget.widgetTemplate.template'),
                    props: props,
                },
                {
                    type: 'form.checkbox',
                    key: 'hasChildren',
                    label: t('widget.widgetLayout.hasChildren'),
                },
            ],
            onLoad: (data) => {
                data.hasChildren = data.children != null;
                return data;
            },
            onSubmit: (data) => {
                if (data.hasChildren) {
                    if (!data.children) {
                        data.children = [];
                    }
                }
                else {
                    if (data.children) {
                        delete data.children;
                    }
                }
                delete data.hasChildren;
                return data;
            },
        }), buildModalTab(props), buildAlertTab(props, { custom: true }), buildHiddenTab(props, { t }), buildStyleTab(props, {
            children: [
                {
                    type: 'form.select',
                    key: 'style.variant',
                    label: t('validation.variant'),
                    values: [
                        { value: 'level1', label: t('widget.widgetTabs.level') + ' 1' },
                        { value: 'level2', label: t('widget.widgetTabs.level') + ' 2' },
                        { value: 'level3', label: t('widget.widgetTabs.level') + ' 3' },
                    ],
                    style: {
                        width: 200,
                    },
                },
            ],
        }), buildEventTab(props, { eventOnLoad: true, eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormModalPanel, Object.assign({}, props))));
};
export const widgetFormModalPanel = {
    type,
    init: (props) => {
        if (props.childProps) {
            if (props.config.children) {
                setModalChildSkipValidate(props.childProps.slice(props.config.children.length));
            }
            else {
                setModalChildSkipValidate(props.childProps);
            }
        }
    },
    load: (props) => {
        handleWidgetOnLoad(props, props.getData());
    },
    update: (props) => {
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairsFromChildProps(props, joinLabels(labelPrefix, props.config.label));
    },
    childConfigs: (config) => {
        if (config.children) {
            return [...config.children, ...config.modal.children];
        }
        else {
            return config.modal.children;
        }
    },
    component: WidgetFormModalPanel,
    icon: React.createElement(BorderAllIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormModalPanelEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, {
        type,
        label: 'Modal Panel',
        modal: { maxWidth: 'xs', fullWidth: true, children: [] },
    }),
};
